import { AttributeType, GeometryType, LayerDefinitionDc } from "@evergis/api";
import { Description, useDialogShadow } from "@evergis/ui";
import React, { FC, memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CENTROID_ATTRIBUTE, GEOMETRY_ATTRIBUTE } from "../../../../evergis/constants";
import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";
import { projectCalculatedAttribute } from "../../../../hooks/attributes/useAddCalculatedAttributes";
import { createGidColumn } from "../../../../utils/createGidColumn";

import { DialogActions } from "../../DialogComponents/DialogActions";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { DialogContent, RestrictedWidthContainer } from "../../DialogComponents/styled";
import { useContext } from "../hooks/useContext";
import { useImportApply } from "../hooks/useImportApply";
import { useImportCancel } from "../hooks/useImportCancel";
import { useImportPolling } from "../hooks/useImportPolling";
import { useLayerFromSchema } from "../hooks/useLayerFromSchema";
import { useImportContext } from "../importContext";
import { ImportMetaPreview } from "../styled";
import { ImportType } from "../types";
import { getLayerDefinition } from "../utils/getLayerDefinition";
import { fileIs } from "./FileLoader/FileLoader";
import { ImportErrors } from "./ImportErrors";
import { ImportSuccess } from "./ImportSuccess";
import { ImportWizard } from "./ImportWizard";
import { ImportProgressBlock } from "./ProgressBlock";

// eslint-disable-next-line max-lines-per-function
const ImportFinishComponent: FC = () => {
  const { t } = useTranslation("common");
  const context = useContext();
  const {
    file,
    prevStep,
    meta,
    attributes,
    dataSchema,
    srFields,
    lonLatInOneField,
    objects,
    importType,
    geometryType,
    sysAttributes,
  } = useImportContext();
  const { alias, description, preview } = meta;
  const isTableSchema = fileIs(file, ["xls", "xlsx", "csv"]);
  const { shadows, setScrollContainer } = useDialogShadow();

  const layerDefinition: LayerDefinitionDc | SchemaParsed = useMemo(
    () => (importType === ImportType.KML ? getLayerDefinition(dataSchema[0].children) : (dataSchema as SchemaParsed)),
    [dataSchema, importType],
  );

  const filteredAttributes = useMemo(() => {
    return attributes.filter(attribute => !attribute.disabled);
  }, [attributes]);

  const formattedSysAttributes = useMemo(() => {
    return sysAttributes.map(sysAttributeItem => {
      const { attributeName, id, ...attribute } = sysAttributeItem;

      return { ...attribute, name: attributeName, originalName: attribute.columnName };
    });
  }, [sysAttributes]);

  const geometryPointAttributes = useMemo(
    () => [
      {
        name: GEOMETRY_ATTRIBUTE,
        alias: GEOMETRY_ATTRIBUTE,
        type: AttributeType.Point,
      },
      {
        name: CENTROID_ATTRIBUTE,
        alias: t("coordinates"),
        type: AttributeType.Point,
        expression: projectCalculatedAttribute(GEOMETRY_ATTRIBUTE, "coordinate"),
        stringFormat: {
          format: "DD.DD",
        },
      },
    ],
    [t],
  );

  const allAttributes = useMemo(() => {
    const resultAttributes = [
      (!layerDefinition.idAttribute || !attributes.find(({ name }) => name === layerDefinition.idAttribute)) &&
        createGidColumn(attributes, srFields.srid),
      ...filteredAttributes,
      ...formattedSysAttributes,
    ].filter(Boolean);

    return importType === ImportType.XLSX &&
      !layerDefinition.geometryAttribute &&
      ((srFields.latitude && srFields.longitude) || srFields.longitudeAndLatitude)
      ? [...resultAttributes, ...geometryPointAttributes]
      : resultAttributes;
  }, [
    attributes,
    filteredAttributes,
    formattedSysAttributes,
    srFields,
    layerDefinition.geometryAttribute,
    geometryPointAttributes,
    importType,
  ]);

  const [{ targetLayerName, targetTableName, loadingLayer: creatingLayer, error: layerError }, createLayer] =
    useLayerFromSchema(
      importType === ImportType.KML
        ? { ...layerDefinition, geometryType }
        : importType === ImportType.XLSX
        ? {
            ...layerDefinition,
            geometryAttribute:
              !layerDefinition.geometryAttribute &&
              ((srFields.latitude && srFields.longitude) || srFields.longitudeAndLatitude)
                ? GEOMETRY_ATTRIBUTE
                : layerDefinition.geometryAttribute,
            geometryType:
              layerDefinition.geometryType === GeometryType.Unknown &&
              ((srFields.latitude && srFields.longitude) || srFields.longitudeAndLatitude)
                ? GeometryType.Point
                : layerDefinition.geometryType,
          }
        : layerDefinition,
      meta,
      allAttributes,
      srFields.srid,
    );

  const sourceName =
    importType === ImportType.KML ? objects.map(({ text }) => text) : (dataSchema as SchemaParsed).sourceName;

  const fileType =
    importType === ImportType.KML ? (dataSchema[0] as SchemaParsed).fileType : (dataSchema as SchemaParsed).fileType;

  const [{ importInProgress, processed, importDone, taskId }, error] = useImportPolling(importType, {
    sourceName,
    attributes: filteredAttributes,
    srData: { lonLatInOneField, srFields },
    targetLayerName,
    fileId: file.id,
    fileType,
  });

  const [applyText, onApply] = useImportApply(
    targetLayerName,
    importDone,
    {
      isTableSchema,
      srData: { lonLatInOneField, srFields },
    },
    createLayer,
  );

  const cancelImport = useImportCancel({
    importInProgress,
    importDone,
    taskId,
    targetLayerName,
    targetTableName,
  });

  const getObjectsCount = useMemo(() => {
    return objects
      ? objects.reduce((result, object) => (object.chip > 0 ? result + Number(object.chip) : result), 0)
      : (dataSchema as SchemaParsed).objectCount;
  }, [objects, dataSchema]);

  const anyError = error?.message || layerError?.message;
  const showImportProgress = !importDone && !anyError;

  useEffect(() => {
    onApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle displayShadow={shadows.upper} extra={<ImportWizard />} />
      <DialogContent ref={setScrollContainer} style={{ paddingTop: "0" }}>
        <RestrictedWidthContainer>
          <ImportMetaPreview>
            {preview.src && <img src={preview.src} alt={alias} width="158" />}
            <div>
              <div>{alias}</div>
              <Description>{description}</Description>
            </div>
          </ImportMetaPreview>
          {showImportProgress && (
            <ImportProgressBlock
              importInProgress={importInProgress}
              layerCreating={creatingLayer}
              estimatedRecords={getObjectsCount}
              processedRecords={processed}
            />
          )}
          {importDone && !anyError && <ImportSuccess objectCount={processed} totalCount={getObjectsCount} />}
          {!!anyError && (
            <ImportErrors error={error.message ? error : layerError} importInProgress={importInProgress} />
          )}
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        isWaiting={!importDone && !anyError}
        applyDisabled={!!anyError}
        applyText={applyText}
        backButtonText={t("import.back", { ns: "dialogs" })}
        onBackButton={prevStep}
        onCancel={cancelImport}
        onApply={onApply}
      />
    </FullScreenDialog>
  );
};

export const ImportFinish = memo(ImportFinishComponent);
